import { Skeleton } from '@sunwebgroup/uds-ui/skeleton'
import cn from 'classnames'
import { eachDayOfInterval, format, isBefore, parseISO } from 'date-fns'
import { isEmpty } from 'ramda'
import { use, useCallback, useMemo } from 'react'
import { Day } from '@/scopes/prices/components/PriceTable/Day'
import { NavigationByWeeksContext } from '@/scopes/prices/components/PriceTable/NavigationByWeeksContext'
import { PriceCell } from '@/scopes/prices/components/PriceTable/PriceCell'
import {
  getPriceRangeSelector,
  isPriceTableLoadingStateSelector,
  pricesResponseSelector,
  selectedPriceIdSelector,
} from '@/scopes/prices/components/selectors'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import styles from './Grid.module.css'
import * as Table from './Table'

export function Grid() {
  const { prices, pricesByDurationAndDateByWeeks } = usePricesSelector(pricesResponseSelector)
  const { dateFrom, dateTo } = usePricesSelector(getPriceRangeSelector)
  const selectedPriceId = usePricesSelector(selectedPriceIdSelector)
  const { selectPrice } = use(PricesActionsContext)
  const { navigationDateFrom, navigationDateTo, numberOfColumns } = use(NavigationByWeeksContext)
  const isLoading = usePricesSelector(isPriceTableLoadingStateSelector)

  const selectedPrice = useMemo(
    () => prices?.find(price => price.id === selectedPriceId),
    [prices, selectedPriceId],
  )

  const onPriceSelect = useCallback(
    (newSelectedPriceId: string) => {
      selectPrice(newSelectedPriceId)
    },
    [selectPrice],
  )

  if (!dateFrom || !dateTo || !navigationDateFrom || !navigationDateTo || !prices || isEmpty(prices)) {
    return (
      <Table.Root className={styles.priceTableRoot} data-qa="price-table">
        <Table.Body className={styles.priceTableBody} role="grid">
          {Array.from({ length: numberOfColumns }, (_, i) => (
            <Skeleton key={i} className={styles.columnSkeleton} />
          ))}
        </Table.Body>
      </Table.Root>
    )
  }

  const datesToDisplay = eachDayOfInterval({ start: navigationDateFrom, end: navigationDateTo }).map(date =>
    format(date, 'yyyy-MM-dd'),
  )

  const today = format(new Date(), 'yyyy-MM-dd')
  const disableColumn = (date: string) => isBefore(parseISO(date), parseISO(today))

  return (
    <Table.Root className={styles.priceTableRoot} data-qa="price-table">
      <Table.Body className={styles.priceTableBody} role="grid">
        {datesToDisplay.map(date => (
          isLoading
            ? <Skeleton key={date} className={styles.columnSkeleton} />
            : (
                <Table.Col
                  key={date}
                  className={cn(styles.priceTableColumn, disableColumn(date) && styles.priceTableColumnDisabled)}
                  role="row"
                >
                  <Table.Header className={styles.dayHeader}>
                    <Day date={date} />
                  </Table.Header>
                  {!disableColumn(date) && Object.keys(pricesByDurationAndDateByWeeks!).map((duration: string) => (

                    <PriceCell
                      key={duration + date}
                      price={pricesByDurationAndDateByWeeks![Number(duration)]?.[date]}
                      onClick={onPriceSelect}
                      isSelected={selectedPrice?.departureDate === date && selectedPrice?.duration.toString() === duration}
                    />
                  ))}
                </Table.Col>
              )
        ))}
      </Table.Body>
    </Table.Root>
  )
}
